// Import images directly for TypeScript safety
import lexicraftImg from '../assets/projects/lexicraft.webp';
import socialNetworkImg from '../assets/projects/social-network.webp';
import rebeltextImg from '../assets/projects/rebeltext.webp';
import josephusImg from '../assets/projects/josephus.webp';
import alphabetCatcherImg from '../assets/projects/alphabet-catcher.webp';
import lexicraft3Img from '../assets/projects/lexicraft3.webp';
import tafakkurimg from '../assets/projects/Tafakkur.webp';
import numreactImg from '../assets/projects/reactionbot.webp';
import htmlCssPortfolioImg from '../assets/projects/old.webp';

// Premium newly added project assets
import clankerfuedImg from '../assets/projects/ClankerFued.webp';
import rhetaiImg from '../assets/projects/rhetai.png';
import medisyncImg from '../assets/projects/MediSync.webp';
import shopverseImg from '../assets/projects/ShopVerse.webp';
import tableegImg from '../assets/projects/Tableeg.webp';

export interface ProjectData {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  fullDescription: string;
  image: string;
  category: string[];
  technologies: string[];
  demoLink?: string;
  codeLink?: string;
  videoLink?: string;
  videoAsset?: string;      // Local video file path in public/assets
  reportLink?: string;      // PDF report link
  readmeContent?: string;   // Full README markdown content
  features: string[];
  challenges: string;
  architecture: string;
  hackathon?: boolean;      // Built in a hackathon
}

export const projects: ProjectData[] = [
  {
    id: 1,
    title: "ClankerFUED",
    subtitle: "AI-Integrated Multiplayer Game Backend",
    description: "An agentic LLM backend enabling real-time, context-aware NPC dialogue for 2+ concurrent players in Unity.",
    fullDescription: "ClankerFUED is a groundbreaking AI-powered multiplayer game featuring interactive NPCs driven by Large Language Models. Built on a non-blocking asynchronous Python framework, the game integrates with Unity to provide multi-turn conversation support. NPCs possess persistent state, short-term memory, and unique personality prompt matrices, reacting to player choices in real-time with sub-300ms latency.",
    image: clankerfuedImg,
    category: ['ai', 'python', 'game'],
    technologies: ['Python', 'Large Language Models', 'Agentic Workflows', 'Asynchronous I/O', 'Unity'],
    codeLink: "https://github.com/Rebelhere/ClankerFUED",
    videoAsset: "/assets/ClankerFued.mp4",
    reportLink: "/assets/ClankerFued-Report.pdf",
    features: [
      "Real-time dynamic conversations between players and LLM-driven NPCs.",
      "Persistent short-term and long-term memory handling to ensure coherent context-aware interactions.",
      "Latency reduction architectures utilizing non-blocking async operations for real-time game play.",
      "Custom prompt engineering schemas allowing highly distinct NPC characters."
    ],
    challenges: "Handling the latency of large model completion APIs in a synchronous multiplayer context was a significant hurdle. We resolved this by shifting to an asynchronous multi-threaded queue backend, utilizing streaming completions and buffering token outputs to feed game frames seamlessly without slowing frame updates in Unity.",
    architecture: "The backend is structured around a Python FastAPI server orchestrating LangGraph workflows, communicating via high-speed WebSockets with a Unity frontend."
  },
  {
    id: 3,
    title: "ShopVERSE",
    subtitle: "Full-Stack Multi-Vendor E-Commerce",
    description: "A secure multi-vendor marketplace featuring modular React layouts and robust FastAPI endpoints.",
    fullDescription: "ShopVERSE is a production-ready, highly responsive multi-vendor e-commerce platform designed to host multiple merchants. The platform separates client-side experiences, vendor management dashboards, and admin oversight, running on containerized Docker environments for seamless scalability.",
    image: shopverseImg,
    category: ['web', 'react', 'sql'],
    technologies: ['React.js', 'FastAPI', 'SQL', 'SQLAlchemy ORM', 'Docker', 'Figma Prototyping'],
    codeLink: "https://github.com/Rebelhere/ShopVERSE",
    features: [
      "Modular components featuring custom state-management and smooth transitions.",
      "15+ fully optimized FastAPI REST endpoints protected by robust input validation and JWT auth.",
      "Highly normalized SQL database schema designed for efficient complex relational queries.",
      "Dynamic filtering, pagination, and instantaneous search functionality."
    ],
    challenges: "Creating a multi-merchant ledger requires intricate transactions to prevent race conditions during checkout. We implemented transactional isolating states in the database and utilized SQLAlchemy's locking systems to maintain perfect transactional consistency.",
    architecture: "Built with React.js for the UI, styled with custom CSS and Bootstrap, and backed by a FastAPI backend running inside Docker containers."
  },
  {
    id: 6,
    title: "MediSync",
    subtitle: "AI Health Network — Hackathon Project",
    description: "A multi-agent AI health platform with wellness coaching, mental health support, and location-aware medical services — built in a hackathon.",
    fullDescription: "MediSync is a premium multi-agent AI health platform built during a hackathon under extreme time constraints. Despite the limited timeframe, the team delivered a fully functional system featuring specialized health agents (Wellness Coach, Mental Health Companion, Local Services Finder), real-time web search citations, interactive maps for nearby medical facilities, and a polished glassmorphism UI. The platform demonstrates what's possible when AI agent orchestration meets rapid prototyping under pressure.",
    image: medisyncImg,
    category: ['ai', 'web', 'react', 'hackathon'],
    technologies: ['React 18', 'Vite', 'FastAPI', 'OpenAI GPT-4o', 'React-Leaflet', 'DuckDuckGo Search', 'Docker', 'Google Cloud Run'],
    codeLink: "https://github.com/Rebelhere/MediSync",
    hackathon: true,
    readmeContent: `# 🏥 MediSync — AI Health Network
http://localhost:5173
gcloud run deploy medisync --image gcr.io/YOUR_PROJECT_ID/medisync --platform managed`,
    features: [
      "Multi-agent AI system with specialized Wellness Coach, Mental Health Companion, and Local Services agents.",
      "Perplexity-style web search citations — fetches real-time data via DuckDuckGo and displays source cards.",
      "Interactive Leaflet map showing nearby clinics, pharmacies, and hospitals via OpenStreetMap Overpass API.",
      "Emergency info panel with reverse-geocoded local emergency numbers.",
      "Premium glassmorphism UI with agent-specific color-coded badges.",
      "Google Cloud Run deployment via multi-stage Docker builds."
    ],
    challenges: "Building a fully functional multi-agent health platform in a hackathon's limited timeframe required extreme prioritization. We focused on core agent orchestration first, then layered in the map and search features. Session memory management across multiple agent contexts was solved using a shared state pool with agent-specific namespaces.",
    architecture: "A FastAPI backend orchestrating multiple OpenAI-powered health agents, with DuckDuckGo web search integration and OpenStreetMap geolocation. The React frontend uses Leaflet for maps and a custom agent-badged chat interface."
  },
  {
    id: 5,
    title: "Tableeg App",
    subtitle: "Full-Stack Quran Study Platform (Solo Project)",
    description: "A solo, under-development full-stack Quran study web app built with React + FastAPI, featuring surah browsing, ayah search, and future AI-driven search expansions.",
    fullDescription: "Tableeg is an active solo project currently under development to deliver a dedicated web application for reading and studying the Quran, built with React (frontend) and FastAPI (backend). Designed as a highly personal research space, the application is being built to integrate sophisticated features, audio recitations, and advanced semantic AI enhancements in the future. The app follows a modular architecture with reusable components, custom hooks, and a structured API layer.",
    image: tableegImg,
    category: ['web', 'react', 'ai', 'sql'],
    technologies: ['React.js', 'FastAPI', 'SQLAlchemy', 'Pydantic', 'Vite', 'Python'],
    codeLink: "https://github.com/Rebelhere/Tableeg",
    readmeContent: `# Quran App
Tableeg/
backend/
frontend/
npm run dev`,
    features: [
      "Complete surah listing and individual ayah reading experience.",
      "Full-text search across the entire Quran with highlighted results.",
      "RESTful API with Pydantic validation and SQLAlchemy ORM for database models.",
      "Modular React architecture with custom hooks, services layer, and reusable components.",
      "Health check and environment-driven configuration for seamless deployment."
    ],
    challenges: "Handling Arabic text rendering and right-to-left (RTL) layout alongside English translations required careful CSS and component design to ensure both scripts display correctly without breaking the layout.",
    architecture: "A FastAPI backend with SQLAlchemy models, Pydantic schemas, and modular router patterns, paired with a React + Vite frontend using a services layer for API communication."
  },
  {
    id: 4,
    title: "Personal Portfolio",
    subtitle: "Modern Agent-Revamped Showcase",
    description: "A fully responsive developer portfolio built with React, Framer Motion, and Glassmorphism design.",
    fullDescription: "My personal developer portfolio. Built using React.js and Framer Motion, this app presents my technical skillsets, academic credentials, and software products in an interactive, stunning glassmorphism shell. Revamped using high-aesthetic visual rules, it is deployed with Vercel CI/CD pipelines.",
    image: htmlCssPortfolioImg,
    category: ['web', 'react'],
    technologies: ['React.js', 'Framer Motion', 'CSS Glassmorphic Tokens', 'Vercel CI/CD'],
    codeLink: "https://github.com/Rebelhere/Portfolio",
    demoLink: "https://muhammadrahim.vercel.app",
    readmeContent: `# Muhammad Rahim — Portfolio (React)
A modern, premium developer portfolio built with **React**, **TypeScript**, **Framer Motion**, and a custom **Glassmorphism** design system.`,
    features: [
      "Stunning, high-end dark slate glassmorphic layout conforming to premium UI/UX rules.",
      "Dynamic mouse hover physics, glowing scrollbars, and customized letter-scattering micro-animations.",
      "Fully responsive architecture (mobile-first) rendering perfectly on all viewport densities.",
      "Continuous Integration and Continuous Deployment pipeline with Vercel."
    ],
    challenges: "Combining complex scroll-reveals with heavy layout calculations can cause frame-drops on low-end mobile devices. We solved this by utilizing hardware-accelerated CSS properties, optimizing canvas renders, and limiting Framer Motion bindings to critical elements.",
    architecture: "A single-page React app (SPA) with localized state-management, custom theme hooks, and client-side page routing."
  },
  {
    id: 7,
    title: "LexiCraft",
    subtitle: "Console-Based Doubly Linked List Text Editor",
    description: "A low-overhead text editor engineered in C++ using a custom doubly linked list data structure.",
    fullDescription: "LexiCraft is a lightweight terminal-based text editor built from scratch in C++. Rather than relying on standard string libraries, it implements a highly optimized custom Doubly Linked List. Each node represents a line of text, allowing O(1) line insertion and deletion, and real-time cursor navigation.",
    image: lexicraftImg,
    category: ['cpp', 'data-structures'],
    technologies: ['C++', 'Doubly Linked List', 'Memory Pointers', 'Terminal Buffer Rendering'],
    codeLink: "https://github.com/Rebelhere/LexiCraft",
    readmeContent: `# Lexicraft
g++ -o lexicraft lexicraft.cpp`,
    features: [
      "Real-time cursor positioning and inline line modifications in a terminal shell.",
      "Fast O(1) text insertions and deletions at arbitrary index nodes.",
      "Persistent state capabilities: write text directly to files and reload buffer arrays.",
      "Robust memory safety controls with custom clean-up routines."
    ],
    challenges: "Managing continuous cursor indexing and matching it to node positions without visual latency was challenging. By decoupling cursor positions from terminal render calls, we achieved clean terminal updates that do not flicker.",
    architecture: "Pure C++ terminal console application utilizing dynamic memory allocations and raw pointer systems."
  },
  {
    id: 8,
    title: "Social Network Application",
    subtitle: "Object-Oriented Network Simulator",
    description: "A terminal social network application demonstrating robust C++ class hierarchies and relationships.",
    fullDescription: "This console-based application simulates a social network system. Users can build profiles, make connections, upload post nodes, and interact on home feeds. It serves as a thorough demonstration of advanced object-oriented programming concepts (OOP) in C++.",
    image: socialNetworkImg,
    category: ['cpp', 'oop'],
    technologies: ['C++', 'Object-Oriented Programming (OOP)', 'Polymorphism', 'Data Networks'],
    codeLink: "https://github.com/Rebelhere/Social-Network-Application",
    readmeContent: `# Social Network Application`,
    features: [
      "Complete user profile setups, friend link databases, and chronological posts feeds.",
      "OOP system employing polymorphism, friend functions, and encapsulation principles.",
      "Comprehensive interactive terminal shell menu with input safety validations."
    ],
    challenges: "Resolving mutual friendship dependencies and avoiding memory leaks during network cascades was crucial. We designed custom destructive methods that cleanly release allocated nodes in the network graph without leaving dangling references.",
    architecture: "C++ modular OOP application built using standard template libraries and custom data maps."
  },
  {
    id: 9,
    title: "RebelText Web App",
    subtitle: "Text Analysis & Formatting Utility",
    description: "A quick, user-friendly utility web application for real-time text parsing, built with Django.",
    fullDescription: "RebelText Web App is a clean, accessible toolkit for analyzing, cleaning, and formatting large bodies of text. Users can remove redundant spaces, convert cases, count readability index criteria, and extract key insights instantaneously in a web terminal.",
    image: rebeltextImg,
    category: ['web', 'django'],
    technologies: ['Python', 'Django Framework', 'HTML5', 'CSS3', 'RegEx Parsers'],
    codeLink: "https://github.com/Rebelhere/rebeltextsite",
    readmeContent: `# RebelTextSite`,
    features: [
      "Text processing engines for case conversion, character counting, and spacing resets.",
      "Rich readability and syntax analysis reports (vowel density, estimated reading times).",
      "Sleek responsive web layout with direct copy-to-clipboard elements."
    ],
    challenges: "Designing fast, responsive string operations for massive blocks of uploaded text could cause thread blocking on the server. We optimized string operations using native Python compile engines (RegEx) to process thousands of words in microseconds.",
    architecture: "A Django application with structured server-side view controllers rendering clean responsive HTML elements."
  },
  {
    id: 10,
    title: "Josephus Problem",
    subtitle: "Circular Elimination Visualizer",
    description: "A beautiful C++ algorithm visualizer using circular queues and SFML for game theory.",
    fullDescription: "An interactive, animated solver for the classic Josephus elimination problem. Developed in C++ using a circular Queue system, it uses the SFML (Simple and Fast Multimedia Library) to visually render the elimination paths, steps, and final survivor in real-time.",
    image: josephusImg,
    category: ['cpp', 'algorithm'],
    technologies: ['C++', 'SFML Library', 'Circular Queues', 'Game Theory Algorithms'],
    codeLink: "https://github.com/Rebelhere/Josephus-Problem",
    features: [
      "Circular grid animation showing active nodes, elimination paths, and survivors.",
      "Customizable step speeds and total survivor variables in an easy-to-use GUI.",
      "Algorithmic implementation using custom circular queue buffer systems."
    ],
    challenges: "Synchronizing state steps between queue node pointer changes and SFML's rendering threads required precise logic. We introduced a time-step delta buffer to guarantee perfectly smooth, frame-synchronized animations.",
    architecture: "Native C++ game visualizer utilizing SFML windowing and rendering pipelines."
  },
  {
    id: 11,
    title: "Alphabet Catcher",
    subtitle: "Assembly Intel 8088 Classic Arcade Game",
    description: "An absolute low-level retro game written entirely in Assembly, designed for the x86 8088 CPU.",
    fullDescription: "CatchAlphabets is an assembly game engineered for x86 Intel 8088 microprocessors. Players control a moving bucket at the bottom of the screen to catch randomly falling letters, using hardware interrupts, keyboard hooks, and direct video memory writing.",
    image: alphabetCatcherImg,
    category: ['assembly', 'game'],
    technologies: ['Intel 8088 Assembly', 'Low-level Interrupt Hooks', 'VGA Video Buffer Rendering', 'Hardware Sound Controllers'],
    codeLink: "https://github.com/Rebelhere/Alphabet-catcher",
    readmeContent: `# CatchAlphabets Game`,
    features: [
      "Dynamic low-level VGA display grid rendering characters, paddles, and score counters.",
      "Direct keyboard mapping using custom DOS interrupts to ensure responsive controls.",
      "Retro speaker sound feedback using high-frequency PC speaker signals."
    ],
    challenges: "Creating standard random number generators at a low-level is challenging without standard OS APIs. We solved this by hooking into the system clock tick interrupt to generate a pseudorandom number stream.",
    architecture: "Pure 8088 Assembly code executed directly on DOSBox emulators."
  },
  {
    id: 12,
    title: "U.S. Higher Education Analysis",
    subtitle: "Data Science & Analytical Models",
    description: "An analytical data project in Jupyter examining enrollment trends and characteristics of US colleges.",
    fullDescription: "An extensive data science analysis project exploring the landscape of U.S. higher education institutions. This project processes multi-featured institutional datasets to identify correlations between tuition pricing, geographic metrics, enrollment quantities, and academic characteristics.",
    image: lexicraft3Img,
    category: ['data-analysis', 'python'],
    technologies: ['Python', 'Jupyter Notebooks', 'Pandas & NumPy', 'Matplotlib & Seaborn', 'Statistical Modeling'],
    codeLink: "https://github.com/Rebelhere/U.S.-Higher-Education-Dataset",
    features: [
      "Clean, multi-stage ETL data preprocessing pipelines handling missing values and anomalies.",
      "Beautiful exploratory data visualizations showing distribution densities and geographic patterns.",
      "Detailed statistical calculations validating enrollment and institutional trend hypotheses."
    ],
    challenges: "The raw educational records contained massive segments of missing values and non-standardized labels. We designed robust filling pipelines, grouping variables by institutional category to preserve analytical integrity.",
    architecture: "Python analytical Jupyter pipelines executing statistical and chart libraries."
  },
  {
    id: 13,
    title: "Numreact Discord Bot",
    subtitle: "Lightweight Automated Discord Reactor",
    description: "A fun Discord bot responding instantly to digit-only messages, utilizing Python asynchronous engines.",
    fullDescription: "Numreact is an automated community engagement Discord bot engineered in Python. It detects numerical strings within channel messages and applies customizable reaction emoji overlays instantly.",
    image: numreactImg,
    category: ['python', 'bot'],
    technologies: ['Python', 'Discord.py Engine', 'Asynchronous Events Processing', 'Bot API Customizations'],
    codeLink: "https://github.com/aatiqqq/Numreact",
    readmeContent: `# 🐄 Discord Custom Emoji Reaction Bot`,
    features: [
      "Fast message parsers filtering character sets and validating digit configurations.",
      "Multi-server state-management enabling customized emoji settings across servers.",
      "Robust error tolerance layers preventing API rate blocks during high-traffic streams."
    ],
    challenges: "Discord API rate limit thresholds are easily tripped if thousands of concurrent channel messages trigger fast reactions. We designed a rate-limit throttler with local message queues to space reactions smoothly.",
    architecture: "Asynchronous Python client using Discord.py event listeners."
  },
  {
    id: 14,
    title: "Old HTML/CSS Portfolio",
    subtitle: "Pure Vanilla Frontend Showcase",
    description: "My legacy developer website crafted from scratch using HTML5, vanilla CSS3, and JavaScript.",
    fullDescription: "Before transitioning to React and advanced AI integrations, this site served as my primary portfolio page. Built entirely with pure vanilla HTML, CSS, and basic JavaScript, it showcases clean layouts, responsive grid patterns, and lightweight page styling.",
    image: htmlCssPortfolioImg,
    category: ['web', 'frontend'],
    technologies: ['HTML5', 'CSS3', 'Vanilla JavaScript', 'Responsive Media Queries'],
    demoLink: "https://rebelhere.github.io/muhammadrahim/",
    codeLink: "https://github.com/Rebelhere/muhammadrahim",
    readmeContent: `# Muhammad Rahim — Portfolio Website`,
    features: [
      "Lightweight structures loading instantly with zero external framework dependencies.",
      "Responsive layout grids dynamically restructuring components via raw media queries.",
      "Clean semantic markup structure ensuring high accessibility scoring.",
      "Light/Dark theme toggle with smooth transitions.",
      "Working contact form powered by Formspree.",
      "Section reveal animations via AOS (Animate on Scroll)."
    ],
    challenges: "Creating smooth cross-browser navigation transitions without libraries like Framer Motion required intricate custom transition rules and fallback styling.",
    architecture: "Static multi-page files connected via absolute relative path layouts."
  },
  {
    id: 15,
    title: "RhetAI",
    subtitle: "AI vs AI Debate Arena",
    description: "Watch intelligent AI agents debate in real-time with automatic evaluation and voice synthesis.",
    fullDescription: "RhetAI is an advanced web-based debate arena where specialized AI agents engage in real-time argumentation over user-defined topics. The platform evaluates debate dynamics using an impartial AI judge and features natural ElevenLabs voice synthesis, interactive scoring visualizations, and custom PDF report compilation.",
    image: rhetaiImg,
    category: ['ai', 'web', 'react'],
    technologies: ['React 18', 'TypeScript', 'Vite', 'Tailwind CSS', 'Framer Motion', 'WebSockets', 'ElevenLabs API', 'jsPDF'],
    codeLink: "https://github.com/rebelhere/rhetai",
    readmeContent: `# RhetAI - AI vs AI Debate Arena`,
    features: [
      "Real-time AI-vs-AI debates with dynamic speech bubbles and round indicators.",
      "Impartial automated judging assessing argument logic, accuracy, and persuasiveness.",
      "Immersive ElevenLabs text-to-speech voice synthesis representing opposing points.",
      "Custom PDF Export engine compiling full debate transcripts and analytical scorecards.",
      "Type-safe React implementation with responsive dark/light mode logo swapping."
    ],
    challenges: "Synchronizing the sequential audio playback of debating AI agents with their visual text bubble highlights was a challenge due to network latency from the Text-to-Speech API. We resolved this by implementing an audio pre-fetching queue hook that buffers the next speaker's audio segment while the current speaker is finishing.",
    architecture: "A frontend single-page application built on Vite, React 18, and TypeScript, communicating with a backend services layer via WebSockets for real-time streaming, and leveraging custom React hooks for ElevenLabs and Google TTS orchestration."
  },
  {
    id: 2,
    title: "Quran & Hadith Platform (Tafakkur)",
    subtitle: "RAG-Augmented Semantic Search Engine",
    description: "An Islamic knowledge search engine built with Node.js and React, querying 6,000+ texts via neural search. Developed as a collaborative group project.",
    fullDescription: "Tafakkur is an advanced search and research platform covering the Quran and 7 major Hadith publications. Developed as a collaborative academic group project, it implements Retrieval-Augmented Generation (RAG) to allow users to ask conceptual, semantic questions (e.g., 'What does the Quran say about modern ethics and kindness?') rather than simple keyword matches. This achieves over 30% higher relevance compared to traditional search algorithms.",
    image: tafakkurimg,
    category: ['web', 'react', 'ai', 'sql'],
    technologies: ['Node.js', 'React.js', 'SQL', 'NLP', 'RAG (Retrieval-Augmented Generation)'],
    codeLink: "https://github.com/StormFs/Tafakkur",
    features: [
      "Advanced semantic/conceptual search covering 6,000+ structured Islamic records.",
      "Highly responsive Node.js REST backend serving queries in under 200ms.",
      "Natural Language Processing (NLP) tokenization, stop-word filters, and deduplication pipeline.",
      "Clean, distraction-free modern UI with responsive dark and light modes."
    ],
    challenges: "Working with antique script translations meant standard sentence embedding models yielded low conceptual match rates. We designed custom NLP tokenization rules, synonym expansion layers, and stop-word dictionaries specifically tailored to Islamic terminology to optimize retrieval precision.",
    architecture: "A Node.js backend triggers a vector semantic search engine combined with traditional SQL relational queries, delivering processed datasets directly to a modular React.js client interface."
  }
];
